import { useState, useEffect, useCallback } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, Trash2 } from "lucide-react";
import { apiGet, apiClient, apiDelete } from "@/lib/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import { translateApiError } from "@/lib/errors/error-map";
import { useAuthStore } from "@/lib/auth/store";
import { CategorySelect } from "@/features/categories/category-select";
import { flattenWithDepth, type Category } from "@/features/categories/types";

interface ProductVariantSummary {
  sell_price: number;
  cost_price: number;
  is_active: boolean;
}

interface Product {
  id: string;
  name: string;
  brand: string;
  category_id: string;
  category_name?: string;
  is_active: boolean;
  variants: ProductVariantSummary[];
}

interface ProductPage {
  data: Product[];
  meta: { total: number; limit: number; offset: number };
}

export function ProductsListPage() {
  const user = useAuthStore((s) => s.user);
  const canMutate = user?.role === "admin" || user?.role === "manager";

  const [search, setSearch] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data: cats } = useQuery({
    queryKey: ["categories"],
    queryFn: () => apiGet<Category[]>("/v1/categories"),
  });

  const isSearching = debouncedQ.trim().length > 0;

  const { data: searchResults, isLoading: searchLoading } = useQuery({
    queryKey: ["products", "search", debouncedQ],
    queryFn: () => apiGet<Product[]>("/v1/products/search", { q: debouncedQ, limit: 50 }),
    enabled: isSearching,
  });

  // BE returns paginated payload as `{data, meta}`; apiGet would strip meta,
  // so call apiClient directly to preserve `meta.total` for pagination.
  const { data: productPage, isLoading: pageLoading } = useQuery({
    queryKey: ["products", "list", page, pageSize, categoryId],
    queryFn: async () => {
      const res = await apiClient.get<ProductPage>("/v1/products", {
        params: {
          limit: pageSize,
          offset: (page - 1) * pageSize,
          ...(categoryId ? { category_id: categoryId } : {}),
        },
      });
      return res.data;
    },
    enabled: !isSearching,
  });

  const products: Product[] = isSearching
    ? (searchResults ?? [])
    : (productPage?.data ?? []);

  const total = isSearching ? products.length : (productPage?.meta?.total ?? 0);
  const totalPages = Math.ceil(total / pageSize);
  const isLoading = isSearching ? searchLoading : pageLoading;

  const catMap = Object.fromEntries(
    flattenWithDepth(cats ?? []).map(({ cat }) => [cat.id, cat.name]),
  );

  const handleCatChange = useCallback((v: string) => {
    setCategoryId(v);
    setPage(1);
  }, []);

  // Soft-delete (is_active=FALSE) — keeps sales history. Manager/admin only.
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: (id: string) => apiDelete(`/v1/products/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["products"] });
      toast.success("Đã xóa sản phẩm");
    },
    onError: (e) => toast.error(translateApiError(e)),
  });

  const handleDelete = useCallback(
    (id: string, name: string) => {
      if (window.confirm(`Xóa sản phẩm "${name}"?`)) del.mutate(id);
    },
    [del],
  );

  function priceRange(variants: ProductVariantSummary[]) {
    if (!variants?.length) return "—";
    const prices = variants.map((v) => v.sell_price).filter(Boolean);
    if (!prices.length) return "—";
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const fmt = (n: number) =>
      new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND" }).format(n);
    return min === max ? fmt(min) : `${fmt(min)} – ${fmt(max)}`;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Sản phẩm</h1>
        {canMutate && (
          <Link
            to="/products/new"
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus size={16} />
            Thêm sản phẩm
          </Link>
        )}
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Tìm tên, thương hiệu, SKU…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <CategorySelect
          value={categoryId}
          onChange={handleCatChange}
          cats={cats ?? []}
          className="w-[240px]"
        />
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Đang tải…</p>
      ) : (
        <>
          <Table>
            <THead>
              <Tr>
                <Th>Tên sản phẩm</Th>
                <Th>Thương hiệu</Th>
                <Th>Danh mục</Th>
                <Th className="text-center">Variants</Th>
                <Th>Giá bán</Th>
                <Th>Trạng thái</Th>
                {canMutate && <Th className="text-right">Thao tác</Th>}
              </Tr>
            </THead>
            <TBody>
              {products.length === 0 && (
                <Tr>
                  <Td colSpan={canMutate ? 7 : 6} className="text-center text-muted-foreground">
                    Không có sản phẩm
                  </Td>
                </Tr>
              )}
              {products.map((p) => (
                <Tr key={p.id}>
                  <Td>
                    <Link
                      to="/products/$id"
                      params={{ id: p.id }}
                      className="font-medium hover:underline"
                    >
                      {p.name}
                    </Link>
                  </Td>
                  <Td>{p.brand || "—"}</Td>
                  <Td>{catMap[p.category_id] ?? "—"}</Td>
                  <Td className="text-center">{p.variants?.length ?? 0}</Td>
                  <Td>{priceRange(p.variants ?? [])}</Td>
                  <Td>
                    <span
                      className={
                        p.is_active
                          ? "rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700"
                          : "rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                      }
                    >
                      {p.is_active ? "Hoạt động" : "Ẩn"}
                    </span>
                  </Td>
                  {canMutate && (
                    <Td className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(p.id, p.name)}
                        disabled={del.isPending}
                        aria-label={`Xóa sản phẩm ${p.name}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </Td>
                  )}
                </Tr>
              ))}
            </TBody>
          </Table>

          {!isSearching && totalPages > 1 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {total} sản phẩm — trang {page}/{totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Trước
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Sau
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
